import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useApp } from "@/contexts/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, ArrowDownCircle, ArrowUpCircle, Wallet } from "lucide-react";

const CUENTAS = ["CAJA", "BANCO_BCP", "BANCO_BBVA", "BANCO_INTERBANK", "BANCO_SCOTIABANK"];
const MEDIOS = ["EFECTIVO", "TRANSFERENCIA", "DEPOSITO", "YAPE", "PLIN", "CHEQUE", "TARJETA"];
const fmt = (n) => Number(n || 0).toLocaleString("es-PE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function CajaBancos() {
  const { empresa, formatApiError } = useApp();
  const [movs, setMovs] = useState([]);
  const [saldos, setSaldos] = useState([]);
  const [open, setOpen] = useState(false);
  const blank = { fecha: new Date().toISOString().slice(0, 10), tipo: "INGRESO", cuenta: "CAJA", concepto: "", monto: 0, moneda: "PEN", medio_pago: "EFECTIVO" };
  const [form, setForm] = useState(blank);

  const load = async () => {
    const [{ data: m }, { data: s }] = await Promise.all([api.get("/caja"), api.get("/caja/saldo")]);
    setMovs(m); setSaldos(s);
  };
  useEffect(() => { if (empresa) load(); /* eslint-disable-next-line */ }, [empresa]);

  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/caja", { ...form, monto: +form.monto }); toast.success("Movimiento registrado"); setOpen(false); setForm(blank); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const eliminar = async (m) => { if (!confirm("¿Eliminar?")) return; await api.delete(`/caja/${m.id}`); toast.success("Eliminado"); load(); };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">Caja y Bancos</h1>
          <p className="text-[13px] text-slate-500">Movimientos de ingresos y egresos por cuenta.</p>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setForm(blank); }}>
          <DialogTrigger asChild><Button data-testid="btn-nuevo-mov" className="h-9 bg-slate-900 text-white"><Plus className="w-4 h-4 mr-1" /> Nuevo movimiento</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Nuevo movimiento</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <Label className="text-[12px]">Fecha</Label>
                <Input type="date" value={form.fecha} onChange={(e) => setForm({ ...form, fecha: e.target.value })} className="h-9" />
              </div>
              <div>
                <Label className="text-[12px]">Tipo</Label>
                <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                  <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="INGRESO">Ingreso</SelectItem><SelectItem value="EGRESO">Egreso</SelectItem></SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[12px]">Cuenta</Label>
                <Select value={form.cuenta} onValueChange={(v) => setForm({ ...form, cuenta: v })}>
                  <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>{CUENTAS.map((c) => <SelectItem key={c} value={c}>{c.replace("_", " ")}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[12px]">Medio de pago</Label>
                <Select value={form.medio_pago} onValueChange={(v) => setForm({ ...form, medio_pago: v })}>
                  <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>{MEDIOS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label className="text-[12px]">Concepto</Label>
                <Input value={form.concepto} onChange={(e) => setForm({ ...form, concepto: e.target.value })} required className="h-9" />
              </div>
              <div>
                <Label className="text-[12px]">Monto</Label>
                <Input type="number" step="0.01" value={form.monto} onChange={(e) => setForm({ ...form, monto: e.target.value })} required className="h-9 tabular text-right" />
              </div>
              <div>
                <Label className="text-[12px]">Moneda</Label>
                <Select value={form.moneda} onValueChange={(v) => setForm({ ...form, moneda: v })}>
                  <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="PEN">PEN</SelectItem><SelectItem value="USD">USD</SelectItem></SelectContent>
                </Select>
              </div>
              <DialogFooter className="col-span-2"><Button type="submit" className="bg-slate-900 text-white">Guardar</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {saldos.length === 0 ? (
          <div className="col-span-4 bg-white border border-slate-200 rounded-md p-6 text-center text-sm text-slate-500">
            <Wallet className="w-8 h-8 mx-auto mb-2 text-slate-300" />Sin saldos aún
          </div>
        ) : saldos.map((s) => (
          <div key={s.cuenta} className="bg-white border border-slate-200 rounded-md p-4">
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">{s.cuenta.replace("_", " ")}</div>
            <div className={`text-xl font-bold tabular ${s.saldo >= 0 ? "text-slate-900" : "text-rose-600"}`}>S/ {fmt(s.saldo)}</div>
          </div>
        ))}
      </div>

      <div className="bg-white border border-slate-200 rounded-md">
        <table className="tbl w-full">
          <thead><tr><th>Fecha</th><th>Tipo</th><th>Cuenta</th><th>Concepto</th><th>Medio</th><th className="text-right">Monto</th><th></th></tr></thead>
          <tbody>
            {movs.length === 0 ? <tr><td colSpan={7} className="text-center py-6 text-slate-500">Sin movimientos</td></tr>
              : movs.map((m) => (
                <tr key={m.id}>
                  <td className="mono">{m.fecha?.slice(0, 10)}</td>
                  <td>{m.tipo === "INGRESO" ? <span className="badge badge-success"><ArrowDownCircle className="w-3 h-3 mr-1" />INGRESO</span> : <span className="badge badge-error"><ArrowUpCircle className="w-3 h-3 mr-1" />EGRESO</span>}</td>
                  <td className="mono">{m.cuenta.replace("_", " ")}</td>
                  <td className="text-slate-800">{m.concepto}</td>
                  <td><span className="badge badge-muted">{m.medio_pago}</span></td>
                  <td className={`tabular text-right font-bold ${m.tipo === "INGRESO" ? "text-emerald-600" : "text-rose-600"}`}>{m.tipo === "INGRESO" ? "+" : "-"} {fmt(m.monto)}</td>
                  <td className="text-right"><button onClick={() => eliminar(m)} className="text-slate-600 hover:text-rose-600"><Trash2 className="w-4 h-4" /></button></td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
